import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, GraduationCap, Clock, Tag } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { COURSES, getServiceBySlug } from "@/data/services";
import PageShell from "@/components/PageShell";
import SectionTitle from "@/components/SectionTitle";
import { useSEO } from "@/hooks/useSEO";

const Training = () => {
  const { t, lang } = useLanguage();
  const service = getServiceBySlug("training");
  useSEO({
    title: t("training_page.title"),
    description: t("training_page.sub"),
    image: service?.cover,
    lang,
  });

  return (
    <PageShell dataTestId="training-page">
      {/* Hero */}
      <section data-on-dark="true" className="relative h-[60vh] md:h-[75vh] overflow-hidden">
        <img
          src={service.cover}
          alt="Formation"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/40 to-[#050505]" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-14 md:pb-20">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="overline kw-accent-line"
          >
            {t("training_page.overline")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-6 font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[1]"
          >
            {t("training_page.title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-6 max-w-2xl text-[#A19E9C] text-base md:text-lg leading-relaxed"
          >
            {t("training_page.sub")}
          </motion.p>
        </div>
      </section>

      {/* Courses */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <SectionTitle
          overline={t("training_page.overline")}
          title={t("training_page.courses_title")}
          testId="training-courses-title"
        />

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {COURSES.map((c, i) => (
            <motion.div
              key={c.key}
              data-testid={`course-card-${c.key}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="group relative border border-[#F2EBE5]/10 bg-[#0a0a0a] p-8 md:p-10 hover:border-[#C5A059]/40 transition-colors"
            >
              <GraduationCap
                className="w-7 h-7 text-[#C5A059] mb-8"
                strokeWidth={1.25}
              />
              <h3 className="font-serif-display text-3xl text-[#F2EBE5] mb-3">
                {t(`courses.${c.key}.title`)}
              </h3>
              <p className="text-sm text-[#A19E9C] leading-relaxed min-h-[4rem]">
                {t(`courses.${c.key}.description`)}
              </p>

              <div className="mt-8 pt-6 border-t border-[#F2EBE5]/10 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] flex items-center gap-2">
                    <Tag className="w-3 h-3" strokeWidth={1.25} />
                    {t("training_page.level")}
                  </span>
                  <span className="text-[#F2EBE5]">
                    {lang === "fr" ? c.level_fr : c.level_en}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] flex items-center gap-2">
                    <Clock className="w-3 h-3" strokeWidth={1.25} />
                    {t("training_page.duration")}
                  </span>
                  <span className="text-[#F2EBE5]">
                    {lang === "fr" ? c.duration_fr : c.duration_en}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#A19E9C]">
                    {t("training_page.price")}
                  </span>
                  <span className="font-serif-display text-xl text-[#C5A059]">
                    {c.price}
                  </span>
                </div>
              </div>

              <Link
                to="/contact?service=training"
                data-testid={`course-cta-${c.key}`}
                className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors"
              >
                {t("training_page.cta_button")}
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-12 gap-3 md:gap-4">
          {service.gallery.slice(0, 4).map((url, i) => (
            <div
              key={url}
              className={`kw-img-frame ${
                i === 0
                  ? "col-span-12 md:col-span-7 aspect-[16/10]"
                  : i === 1
                    ? "col-span-12 md:col-span-5 aspect-[16/10]"
                    : "col-span-6 md:col-span-6 aspect-[4/3]"
              }`}
            >
              <img src={url} alt={`formation ${i + 1}`} loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-28 border-t border-[#C5A059]/20 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h3 className="font-serif-display text-3xl md:text-5xl leading-tight">
              {t("training_page.cta_title")}
            </h3>
            <p className="mt-4 text-[#A19E9C]">
              {t("training_page.cta_sub")}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact?service=training"
              data-testid="training-cta-button"
              className="kw-btn-primary"
            >
              {t("training_page.cta_button")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Training;
